import React, { useCallback, useMemo } from 'react';
import useAnalyticsStore from '../../../../../store/analyticsStore';
import useDatasetStore from '../../../../../store/datasetStore';
import useHomeStore from '../../../../../store/homeStore';
import { usePointStore } from '../../../../../store/pointStore';
import { DownloadInfoItem, IBestDataset } from '../../../../../types/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../../types/chart';
import { IAnalytcisUserInput } from '../../../../../types/IAnalyticsUserInput';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { isOfflineDatasetSelectionGuard } from '../../../../../utils/offlineDatasetGuard';
import AltimeterComparison from '../../../ResultPanel/Plot/AltimeterComparison';

export default function SatelliteTab(props: {
  downloadItems: DownloadInfoItem[];
  activePointId: string;
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointName: string;
  datasetKey: string;
  gridCellSize: string;
  waterDepth: string;
  activePanel: string;
  activePlot: string[];
  setActivePlot: (value: string[]) => void;
  advancedAnalytics: any;
  userInput: IAnalytcisUserInput;
  updateUserInput: (update: Partial<IAnalytcisUserInput>) => void;
  isLoadingExtraction: boolean;
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
}) {
  const enrichedMetadata = useAnalyticsStore((state) => state.enrichedMetadata);
  const setIsChangeDatasetWarningVisible = useHomeStore(
    (state) => state.setIsChangeDatasetWarningVisible
  );
  const setTemporaryDatasetName = useHomeStore(
    (state) => state.setTemporaryDatasetName
  );
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const fwePoints = usePointStore((state) => state.fwePoints);

  const onDatasetChange = (value: string) => {
    if (
      isOfflineDatasetSelectionGuard({
        datasetId: value,
        datasetBriefs,
        setDatasetIsOfflineWarningVisible:
          props.setDatasetIsOfflineWarningVisible,
      })
    ) {
      return;
    }

    setTemporaryDatasetName(value);
    setIsChangeDatasetWarningVisible(true);
  };

  const isFWE = useMemo(() => {
    const findFwe = datasetBriefs?.find(
      (item) => props.previewDatasetId === item.id && item.isFWE
    );

    return !!findFwe;
  }, [props.previewDatasetId, datasetBriefs]);

  const fwePointName = useMemo(() => {
    const findFwePoint = fwePoints?.find((item) => {
      return (
        props.coordinates?.[0] === Number(item.position[0].toFixed(6)) &&
        props.coordinates?.[1] === Number(item.position[1].toFixed(6))
      );
    });
    return findFwePoint?.name || '';
  }, [props.coordinates, fwePoints]);

  const onUpdateAltimeterComparison = useCallback(
    async (payload: IAdvancedAnalyticsApiPayload) => {
      try {
        await props.advancedAnalytics.updateAltimeterComparison({
          ...payload,
          PointName: isFWE ? fwePointName : payload.PointName,
          IsFwe: isFWE,
          PlotFlag: '[1 3]',
        });
      } catch (error) {
        console.error('Error getting advanced analytics data:', error);
      }
    },
    [isFWE, fwePointName, props.advancedAnalytics.updateAltimeterComparison]
  );

  const datasets = useMemo((): IBestDataset[] => {
    return (
      props.downloadItems.find((dp) => dp.radioKey === props.datasetKey)
        ?.options ?? []
    );
  }, [props.downloadItems, props.datasetKey]);

  if (props.activePlot.includes('Altimeter Comparison') && enrichedMetadata)
    return (
      <div style={{ height: '100%' }}>
        <AltimeterComparison
          altimeterComparisonImage={props.advancedAnalytics.images.altimeter}
          coordinates={props.coordinates}
          datasets={datasets}
          enrichedMetadata={enrichedMetadata}
          error={props.advancedAnalytics.errors.altimeter}
          onUpdate={onUpdateAltimeterComparison}
          isUpdating={
            props.advancedAnalytics.isUpdating || props.isLoadingExtraction
          }
          previewDatasetId={props.previewDatasetId}
          previewDatasetTimeRange={
            props.userGroupPermissions?.validationDateRange
          }
          pointName={props.pointName}
          activePanel={props.activePanel}
          datasetChange={onDatasetChange}
          userInput={props.userInput}
          updateUserInput={props.updateUserInput}
        />
      </div>
    );

  return <div style={{ height: '100%' }}></div>;
}
